import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { useGetResourceById } from '_api';
import { ResourceRouteMap } from '_constants';
import { StyledDetailDescription, ThumbnailView } from 'components/atoms';
import React from 'react';
import { FaEdit } from 'react-icons/fa';
import { FaArrowUpRightFromSquare } from 'react-icons/fa6';
import { IoIosArrowForward, IoMdArrowBack } from 'react-icons/io';
import { useNavigateTo } from 'utils';

import { DetailLink } from './styles';
import { DetailViewProps } from './types';

export const ResourceDetail = ({ id }: DetailViewProps) => {
  const navigate = useNavigateTo();
  const theme = useTheme();
  const { data: resource, isFetching: isLoading } = useGetResourceById(id);

  if (!resource && !isLoading) {
    return <Typography variant="h6">Resources not found...</Typography>;
  }

  const handleBack = () => {
    history.back();
  };

  const handleOnEdit = () => {
    navigate(ResourceRouteMap.EDIT, id.toString());
  };

  function isValidHttpUrl(string: string) {
    let url;
    try {
      url = new URL(string);
    } catch (_) {
      return false;
    }
    return url.protocol === 'http:' || url.protocol === 'https:';
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', paddingTop: '50px' }}>
      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
        <Box sx={{ marginTop: '10px', color: `${theme.palette.primary.main}` }}>
          <IoMdArrowBack />
        </Box>
        <Button onClick={handleBack} sx={{ justifyContent: 'left' }}>
          {' '}
          Back{' '}
        </Button>
      </Box>
      {resource && !isLoading ? (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant={'h3'} gutterBottom>
              {resource.title}
            </Typography>
            <Tooltip title={'Edit'} placement="top">
              <span>
                <IconButton
                  onClick={handleOnEdit}
                  sx={{
                    color: theme.palette.primary.main,
                    paddingLeft: '10px',
                    marginBottom: '20px',
                  }}
                >
                  <FaEdit />
                </IconButton>
              </span>
            </Tooltip>
          </Box>
          <Box
            sx={{
              width: '100%',
              margin: 'auto',
              marginTop: '2rem',
              marginBottom: '2rem',
            }}
          >
            <ThumbnailView fullImageUrl={resource.fullImageUrl} placeholder={true} />
          </Box>
          <StyledDetailDescription variant="body1" sx={{ marginBottom: '16px' }}>
            {resource.description}
          </StyledDetailDescription>
          {isValidHttpUrl(resource.fullDocumentUrl) && (
            <DetailLink
              href={resource.fullDocumentUrl}
              underline="hover"
              target="_blank"
              sx={{ typography: 'h6', width: 'fit-content' }}
            >
              <Box sx={{ display: 'flex', fontSize: '1rem', fontWeight: '400' }}>
                LEARN MORE
                <Box sx={{ padding: '0.1rem', fontWeight: '600' }}>
                  <IoIosArrowForward />
                </Box>
              </Box>
            </DetailLink>
          )}
          {isValidHttpUrl(resource.url) && (
            <DetailLink
              href={resource.url}
              underline="hover"
              target="_blank"
              sx={{ typography: 'h6', width: 'fit-content' }}
            >
              <Box
                sx={{
                  display: 'flex',
                  fontSize: '1rem',
                  fontWeight: '400',
                  paddingTop: '30px',
                }}
              >
                MORE DETAILS
                <Box sx={{ padding: '0.1rem', fontWeight: '600', paddingLeft: '10px' }}>
                  <FaArrowUpRightFromSquare />
                </Box>
              </Box>
            </DetailLink>
          )}
        </>
      ) : (
        <Box sx={{ margin: 'auto' }}>
          <CircularProgress size={'5rem'} />
        </Box>
      )}
    </Box>
  );
};
